import { ChangeDetectorRef, Component } from '@angular/core'
import { BaseEventListComponent } from '../event-shared/base-event-list.component'
import { EventModel } from '@shared/models/event.model'
import { AuthService } from '@shared/services/auth.service'
import { EventService } from '@shared/services/event.service'
import { AttendeesService } from '@shared/services/attendees.service'
import { PublicPageTitleService } from '@public/app/services/public-page-title.service'
import { LoadingIndicatorService } from '../../services/loading-indicator.service'
import { ToastService } from '../../services/toast.service'
import { callableErrorMessage } from '@shared/errors/callable-error-message'
import moment from 'moment'
import { EventFilter } from '@public/app/components/event-filter/event-filter.component'

@Component({
  selector: 'app-event-list',
  templateUrl: './event-list.component.html',
  styleUrls: ['./event-list.component.scss'],
  standalone: false,
})
export class EventListComponent extends BaseEventListComponent {
  showRegistrationModal = false
  selectedEvent: EventModel | null = null
  isRegisteredModal = false
  userName = ''
  userEmail = ''
  pageTitle = 'Önkéntes események'

  private originalEventList: EventModel[] = []
  private currentFilters: EventFilter = { fromDate: '', toDate: '', text: '' }

  constructor(
    authService: AuthService,
    eventService: EventService,
    attendeesService: AttendeesService,
    publicPageTitleService: PublicPageTitleService,
    private cdr: ChangeDetectorRef,
    loadingIndicatorService: LoadingIndicatorService,
    toastService: ToastService
  ) {
    super(authService, eventService, attendeesService, publicPageTitleService, loadingIndicatorService, toastService)
  }

  protected transformEvents(events: EventModel[]): EventModel[] {
    this.originalEventList = [...events]
    return this.applyFiltersAndReturn()
  }

  private applyFiltersAndReturn(): EventModel[] {
    const { fromDate, toDate, text } = this.currentFilters
    let filtered = [...this.originalEventList]

    if (fromDate) {
      const from = moment(fromDate)
      filtered = filtered.filter(e => moment(e.fromDateTime).isSameOrAfter(from, 'day'))
    }
    if (toDate) {
      const to = moment(toDate)
      filtered = filtered.filter(e => moment(e.fromDateTime).isSameOrBefore(to, 'day'))
    }
    if (text.trim()) {
      const txt = text.trim().toLowerCase()
      filtered = filtered.filter(e =>
        [e.name, e.description, e.city, e.address].some(f => (f || '').toLowerCase().includes(txt))
      )
    }
    return filtered
  }

  onFiltersChange(filters: EventFilter) {
    this.currentFilters = filters
    this.eventList = this.applyFiltersAndReturn()
    this.cdr.markForCheck()
  }

  openRegistrationModal(event: EventModel): void {
    this.selectedEvent = event
    this.isRegisteredModal = !!event.isRegistered
    this.showRegistrationModal = true
    const currentUser = this.authService.getCurrentUser()
    this.userName = currentUser ? currentUser.name : 'Ismeretlen felhasználó'
    this.userEmail = currentUser ? currentUser.email : 'Ismeretlen felhasználó'
  }

  cancelModal(): void {
    this.showRegistrationModal = false
    this.selectedEvent = null
    this.isRegisteredModal = false
  }

  confirmRegistration(): void {
    this.loadingIndicatorService.showLoading()
    const selectedEvent = { ...this.selectedEvent }
    if (!this.authService.getCurrentUser() || !selectedEvent) {
      console.log('Missing feature: handle no user or event')
      this.loadingIndicatorService.hideLoading()
      return
    }
    this.attendeesService
      .goToEvent(this.authService.getCurrentUser().id, selectedEvent.id, selectedEvent.organiserId, this.userName)
      .subscribe({
        next: result => {
          this.showUpdated({
            ...selectedEvent,
            isRegistered: true,
            numberOfAttendees: result?.numberOfAttendees ?? selectedEvent.numberOfAttendees + 1,
          })
          this.toastService.showSuccess('Sikeres jelentkezés')
        },
        error: err => {
          const errorMsg = callableErrorMessage(err)
          this.loadingIndicatorService.hideLoading()
          this.toastService.showError(errorMsg)
        },
        complete: () => {
          this.loadingIndicatorService.hideLoading()
        },
      })
  }

  public get isLoggedIn(): boolean {
    return this.authService.isLoggedIn()
  }

  protected updateEventListAfterUnregister(event: EventModel): void {
    this.showUpdated(event)
  }

  private showUpdated(event: EventModel): void {
    const update = (list: EventModel[]) => {
      const idx = list.findIndex(e => e.id === event.id)
      if (idx !== -1) {
        list[idx] = { ...list[idx], isRegistered: event.isRegistered, numberOfAttendees: event.numberOfAttendees }
      }
    }
    update(this.originalEventList)
    update(this.eventList)
    this.cdr.markForCheck()
  }
}
